<template src="./listbox.html"></template>

<script lang="ts">
import { debounce } from 'util/ts-utils'
import { isIOS, isSafari } from 'util/browser-utils'
import { localizedThousandsInteger } from 'util/localization-utils'
import { nextTick } from 'vue'

export default {
  data() {
    return {
      activeIndex: -1,
      tempTriggerDisable: false,
      ident: Math.random().toString(16).slice(5),
      isExpanded: false,
      searchVal: '',
      value: '',  // for single select
      values: [] // for multi-select
    }
  },
  props: {
    // when true, selecting a value that is already selected, deselects it.  When false, the value
    // remains selected.  This is to handle the disctinction between sort and a single value filter
    // such as "verified purchaser".
    // For sort, this is false.  For filters it is true.
    deselectable: Boolean,
    disabled: Boolean,  // truly disabled. cannot interact with
    isRating: Boolean,
    label: String,
    modelValue: [Array, String, Number, Boolean],
    multiSelect: Boolean,
    options: { type: Array, required: true },
    showCounts: Boolean,
    subLabel: String
  },
  emits: ['update:modelValue'],
  computed: {
    activeDesc() {
      return this.activeIndex > -1 ? this.optionsData[this.activeIndex].id  : null
    },
    btnValue() {
      if (this.subLabel) {
        return this.subLabel
      }
      // assumption is subLabel is always populated for multiSelect listbox.  This only checks
      // for this.value
      const selectedOption = this.options.find((opt) => {
        return opt.value === this.value
      })
      return selectedOption ?
        (selectedOption.labelKey ? this.$msg(selectedOption.labelKey) : selectedOption.label) : ''
    },
    hasSelection() {
      return !!this.value || this.values.length > 0
    },
    optionsData() {
      return this.options.map((opt, i) => {
        const isSelected = this.value === opt.value || this.values.indexOf(opt.value) > -1
        return {
          id: `tt-option-${this.ident}-${i}`,
          label: (opt.labelKey ? this.$msg(opt.labelKey) : opt.label) +
                 (this.showCounts ? ` (${localizedThousandsInteger(opt.count)})` : ''),
          isSelected,
          iconName: this.multiSelect && isSelected ? 'checkbox--on' :
            (this.multiSelect && !isSelected ? 'checkbox--off' :
              (!this.multiSelect && isSelected ? 'check' : '')),
          iconTitle: isSelected ? this.$msg('listbox.check') : '',
          ratingCount: this.isRating && this.showCounts ?
            ` (${localizedThousandsInteger(opt.count)})` : ''
        }
      })
    }
  },
  methods: {
    handleKeydown(e: KeyboardEvent) {
      if (e.keyCode === 9) { // tab - still want to allow tab to continue, so not stop or prevent
        this.setExpanded(false)
      } else if (e.keyCode === 27) { // esc
        e.stopPropagation()
        this.setExpanded(false)
      } else if (e.keyCode === 38) { // arrow up
        e.preventDefault()
        this.moveUp()
      } else if (e.keyCode === 40) { // arrow down
        e.preventDefault()
        this.moveDown()
      } else if (e.keyCode === 36) { // home
        e.preventDefault()
        this.activeIndex = 0
      } else if (e.keyCode === 35) { // end
        e.preventDefault()
        this.activeIndex = this.options.length - 1
      } else if (e.keyCode === 32 || e.keyCode === 13) { // space bar or return
        e.preventDefault()
        this.selectOption(this.activeIndex)
      } else {
        this.searchVal = this.searchVal + String.fromCharCode(e.keyCode).toLowerCase()
        this.findByText()
      }
    },
    findByText: debounce(function cb() {
      const lastItemIndex = this.optionsData.length - 1
      let currentIndex = this.activeIndex === lastItemIndex ? 0 : this.activeIndex + 1
      for (let i = 0; i < lastItemIndex; i++) {
        if (this.optionsData[currentIndex].label.toLowerCase().indexOf(this.searchVal) === 0) {
          this.activeIndex = currentIndex
          break
        }
        currentIndex = currentIndex === lastItemIndex ? 0 : currentIndex + 1
      }
      this.searchVal = ''
    }, 300),
    moveDown() {
      if (this.activeIndex < this.options.length - 1) {
        this.activeIndex = this.activeIndex + 1
      }
    },
    moveUp() {
      if (this.activeIndex > 0) {
        this.activeIndex = this.activeIndex - 1
      }
    },
    onListBlur(e) {
      if (isIOS || isSafari) {
        this.tempTriggerDisable = true
        setTimeout(() => {
          this.tempTriggerDisable = false
        }, 500)
      }

      // this logic helps for the case where the user click on the trigger button.
      // shouldn't usually happen and does not work right in Safari (and IE?)
      // explicitOriginalTarget is checked for Firefox
      if ((e.relatedTarget && this.$el.contains(e.relatedTarget)) ||
          (e.explicitOriginalTarget && this.$el.contains(e.explicitOriginalTarget) &&
           e.target !== e.explicitOriginalTarget)) {
        return
      }
      this.setExpanded(false)
    },
    onTriggerClicked() {
      if ((isIOS || isSafari) && this.tempTriggerDisable) {
        return
      }
      // setting to opposite of current in case user clicks on it again when its opened
      this.setExpanded(!this.isExpanded)
    },
    selectOption(index) {
      const isSelected = this.optionsData[index].isSelected
      const val = this.options[index].value
      if (!this.deselectable) {
        this.value = val
        this.$emit('update:modelValue', this.value)
        nextTick(() => this.setExpanded(false))
        return
      } else if (!this.multiSelect) { // single value filter
        this.value = isSelected ? '' : val
        this.$emit('update:modelValue', this.value)
      } else if (isSelected) { // multi-value filter where this value is being deselected
        this.values.splice(this.values.indexOf(val), 1)
        this.$emit('update:modelValue', this.values)
      } else { // multi-value filter where this value is being selected
        this.values.push(val)
        this.$emit('update:modelValue', this.values)
      }
      this.activeIndex = index
    },
    setValues() {
      if (this.modelValue !== undefined) {
        if (this.multiSelect) {
          this.values = this.modelValue
        } else {
          this.value = this.modelValue
        }
      }
    },
    setExpanded(expand) {
      if (this.disabled && expand === true) {
        return
      }
      this.isExpanded = expand
      if (!this.isExpanded) {
        this.activeIndex = -1
        // make sure list is closed before trying to focus.  Was not working on FF and Safari
        nextTick(() => this.$refs.trigger.focus())
        return
      }
      nextTick(() => {
        // focus goes on the list (ul). The appearance of focus on individual items (li) is
        // achieved with styling and with 'aria-activedescendant' on the list (ul)
        this.$refs.list.focus()
        let firstValue = this.value
        if (this.values.length > 0) {
          firstValue = this.values[0]
        }
        this.activeIndex = !firstValue ? 0 :
          this.activeIndex = this.options.findIndex(opt => opt.value === firstValue)
      })
    }
  },
  watch: {
    // in Vue 3, arrays in watch must have deep: true
    modelValue: {
      handler() {
        this.setValues()
      },
      deep: true
    }
  },
  created() {
    this.setValues()
  }
}
</script>

<style lang="scss" src="./listbox.scss"></style>
